import React from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, Users, ArrowRight, ShieldCheck, ShoppingBag, Globe } from 'lucide-react';
import MyLayout from './Layout';
import { useMyAuth } from '../../context/MyAuthContext';

const ROLE_LABELS = {
  client: 'Client',
  editeur: 'Éditeur',
  manager: 'Manager',
  admin: 'Administrateur',
  superadmin: 'Super Administrateur',
};

function roleLevel(role) {
  return ['client', 'editeur', 'manager', 'admin', 'superadmin'].indexOf(role);
}

export default function MyDashboard() {
  const { user } = useMyAuth();
  const level = roleLevel(user?.role || 'client');

  const cards = [
    {
      to: '/my/actualites',
      icon: Newspaper,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      title: 'Actualités METERIS',
      desc: 'Gérer les articles — publication programmée, recherche, brouillons.',
      minLevel: 1,
    },
    {
      to: '/my/boutique',
      icon: ShoppingBag,
      color: 'text-[#0099D6]',
      bg: 'bg-[#0099D6]/10',
      title: 'Boutique Atelier Météris',
      desc: 'Gérer les produits reconditionnés et les réservations.',
      minLevel: 1,
    },
    {
      to: '/my/sites',
      icon: Globe,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      title: "Sites de l'écosystème",
      desc: 'Accéder aux différents sites du groupe METERIS.',
      minLevel: 1,
    },
    {
      to: '/my/utilisateurs',
      icon: Users,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      title: 'Utilisateurs',
      desc: 'Gérer les comptes et les rôles.',
      minLevel: 3,
    },
  ].filter(c => level >= c.minLevel);

  return (
    <MyLayout title="Tableau de bord">
      <div className="space-y-8">
        {/* Bienvenue */}
        <div className="bg-[#12141a] border border-white/5 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#0099D6]/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#0099D6]" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg">
                Bienvenue{user?.name ? `, ${user.name.split(' ')[0]}` : ''} !
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Vous êtes connecté en tant que{' '}
                <span className="text-white font-medium">{ROLE_LABELS[user?.role] || 'Client'}</span>.
              </p>
              {user?.role === 'client' && (
                <p className="text-gray-500 text-sm mt-2">
                  Votre compte est actif. Contactez l'administrateur pour obtenir des droits supplémentaires.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Raccourcis selon le rôle */}
        {cards.length > 0 && (
          <div>
            <h3 className="text-gray-400 text-xs uppercase tracking-widest mb-4">Accès rapide</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cards.map(card => (
                <Link
                  key={card.to}
                  to={card.to}
                  className="bg-[#12141a] border border-white/5 rounded-2xl p-5 hover:border-white/10 hover:bg-[#15171e] transition-all group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center`}>
                      <card.icon className={`w-5 h-5 ${card.color}`} />
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors" />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{card.title}</h4>
                  <p className="text-gray-500 text-xs">{card.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </MyLayout>
  );
}
